import { useState, type MouseEvent } from "react";
import { ApiError } from "../../api/client";
import { touchScreen } from "../../lib/touch";
import type { Authorization, Order, PrintNotice, Route } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { formatDate, formatTime } from "../../lib/time";
import { printToasts, reprintToast } from "../printing/print-notices";
import { printAgain } from "../printing/reprint";
import { CheckoutDialog } from "../sale/CheckoutDialog";
import { AdminCodeDialog } from "./AdminCodeDialog";
import { CancelDialog } from "./CancelDialog";
import { CorrectionDialog } from "./CorrectionDialog";
import { DeleteDialog } from "./DeleteDialog";
import { DeleteManyDialog } from "./DeleteManyDialog";
import { MailsDialog } from "./MailsDialog";
import { PlatformLogo } from "./PlatformLogo";
import {
  actionsFor,
  addressOf,
  canDelete,
  canRoute,
  correctablePlatform,
  editNeedsCode,
  EditNotes,
  KIND_FILTERS,
  kindOf,
  matchesKind,
  matchesMethod,
  METHOD_FILTERS,
  MethodChangeNote,
  type KindFilter,
  type MethodFilter,
  paymentOf,
  StatusPill,
} from "./order-view";
import { shiftDay } from "./days";
import { OrderPanel } from "./OrderPanel";
import { OrdersOverview } from "./OrdersOverview";
import { PaymentMethodDialog } from "./PaymentMethodDialog";
import { ReceiptDialog } from "./ReceiptDialog";
import { ReceiptsDialog } from "./ReceiptsDialog";
import { RouteDialog } from "./RouteDialog";
import { clickRow, keepExisting, NOTHING, tickAll, tickRow, type Selection } from "./selection";
import type { OrdersState } from "./useOrders";

/** At most 9 addresses per route: what Google Maps takes in one link (prototype). */
const MAX_ROUTE = 9;

type Open =
  | { kind: "pay"; orderId: string }
  | { kind: "cancel" | "correct" | "delete"; orderId: string; authorization: Authorization }
  // another payment method needs no code (Furkan, 07/10/2026); a Takeaway or Uber Eats order the owner's
  // (Antalya, 17/10/2026)
  | { kind: "method"; orderId: string; authorization?: Authorization }
  | { kind: "route"; route: Route }
  | { kind: "receipt"; orderId: string; printed: PrintNotice | undefined }
  | { kind: "mails"; orderId: string }
  | { kind: "receipts"; orderIds: string[] }
  | { kind: "adminCode" }
  // several chosen orders deleted in one go, with the owner's code given once
  | { kind: "deleteMany"; orderIds: string[]; authorization: Authorization }
  | null;

interface Props {
  state: OrdersState;
  /** Reopens the order on the sale screen for editing, with the code that allowed it if it was paid. */
  onEdit: (order: Order, authorization: Authorization | null) => void;
  /** Beheer › Bestellingen: the same list, plus Verwijderen for the owner. */
  admin?: boolean;
  /**
   * The owner opened Beheer (with this code): a small ⚙ next to the title asks for the special admin
   * code from Tafo, and only then shows Verwijderen.
   */
  ownerBeheer?: Authorization | undefined;
  /**
   * Beheer › Bestellingen per dag: the day shown and the small calendar above the list. Another day than
   * today is looked at: no Annuleren; Verwijderen only behind the owner's ⚙.
   */
  day?: { value: string; today: string; onChange: (day: string) => void };
}

/**
 * Today's orders, live from every till and app of the shop, as in the prototype (renderOrdersIn): the
 * list on the left, on the right the receipt of the chosen order or, with several chosen, their totals.
 * A click chooses one order, Ctrl/⌘-click or the boxes several, Shift-click a range; with a selection
 * the bar changes the payment method of all of them, starts a route along the deliveries or prints the
 * receipts. A double click opens an order for editing.
 */
export function OrdersScreen({ state, onEdit, admin = false, ownerBeheer, day }: Props) {
  const { api, authorize, report, toast } = useTill();
  const [chosen, setChosen] = useState<Selection>(NOTHING);
  const [open, setOpen] = useState<Open>(null);
  const [busy, setBusy] = useState(false);
  /**
   * Verwijderen stays out of sight until the owner taps the small ⚙ and enters the special admin code
   * from Tafo (Furkan, 01/10/2026). The code stays in memory only while Beheer is open.
   */
  const [adminCode, setAdminCode] = useState<string | null>(null);
  /** Soort and Betaalwijze above the list (Furkan, 11/10/2026). */
  const [kind, setKind] = useState<KindFilter>("all");
  const [method, setMethod] = useState<MethodFilter>("all");
  /** A phone: the two rows of filters only after a tap on "Filter" (Furkan, 19/10/2026). */
  const [filtersOpen, setFiltersOpen] = useState(false);
  /** An earlier day in Beheer: to look at and print; only the owner's ⚙ still removes an order. */
  const earlier = day !== undefined && day.value !== day.today;
  // the ⚙ stays on earlier days too (Furkan, 11/10/2026: "tandwieltje voor verwijderen moet er terug bij")
  const showDelete = adminCode !== null;
  /** The orders as filtered: what the list shows, and what can be chosen. */
  const shown = state.orders.filter((order) => matchesKind(order, kind) && matchesMethod(order, method));
  const filtered = kind !== "all" || method !== "all";
  const ids = shown.map((order) => order.id);
  const selection = keepExisting(chosen, ids);
  const selected = shown.filter((order) => selection.ids.has(order.id));
  const active = selected.filter((order) => order.status !== "cancelled");
  // het totaal van de getoonde (gefilterde) lijst, zonder geannuleerde — getoond in Beheer (Furkan, 15/10/2026)
  const shownActive = shown.filter((order) => order.status !== "cancelled");
  const listTotalCents = shownActive.reduce((total, order) => total + order.totalCents, 0);
  const routable = selected.filter(canRoute);
  const allOn = shown.length > 0 && selected.length === shown.length;
  const byId = (id: string) => state.orders.find((order) => order.id === id) ?? null;
  const dialogOrder = open !== null && "orderId" in open ? byId(open.orderId) : null;
  const close = () => {
    setOpen(null);
  };
  const only = (order: Order) => {
    setChosen({ ids: new Set([order.id]), last: order.id });
  };

  /** Bewerken: an unpaid order right away; a paid one after a code with the right to edit. */
  const startEdit = async (order: Order) => {
    if (!editNeedsCode(order)) {
      onEdit(order, null);
      return;
    }
    const authorization = await authorize("orders.edit", t.codes.editPaid);
    if (authorization) onEdit(order, authorization);
  };

  const withCode = async (kind: "cancel" | "correct" | "delete", order: Order) => {
    only(order);
    const authorization =
      kind === "cancel"
        ? await authorize("orders.cancel", t.codes.cancel)
        : kind === "correct"
          ? await authorize("orders.delete", t.codes.correct)
          : await authorize("orders.delete", t.codes.delete);
    if (authorization) setOpen({ kind, orderId: order.id, authorization });
  };

  /**
   * "→ Afhaal" on a table, only behind the ⚙ and the special admin code (Furkan, 15/10/2026: "dit zijn heel
   * uitzonderlijke gevallen"): taken at a table, but it was to take away. One click: the server prices it as
   * a takeaway, with the VAT of a takeaway, also when it was paid, and keeps nothing of it in its history.
   */
  const toTakeaway = async (order: Order) => {
    if (ownerBeheer === undefined || adminCode === null || busy) return;
    only(order);
    setBusy(true);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/table-to-takeaway`, {
        staffToken: ownerBeheer.token,
        headers: { "x-admin-code": adminCode },
        body: { expectedRevision: order.revision },
      });
      // at once in the list, also on an earlier day where nothing live follows (Furkan, 30/10/2026: "bazen 2-3
      // kerede calisiyor anca": the row stayed a table and the next press was refused)
      state.upsert(updated);
      toast(t.orders.toTakeaway.done(order.table ?? ""));
    } catch (error) {
      // pressed again before the row followed: the order as it is now; a takeaway already is no error
      const behind =
        error instanceof ApiError && (error.code === "to_takeaway_not_table" || error.code === "order_changed");
      const now = behind ? await api.request<Order>("GET", `/orders/${order.id}`).catch(() => null) : null;
      if (now !== null) state.upsert(now);
      if (now?.channel === "takeaway") toast(t.orders.toTakeaway.done(order.table ?? ""));
      else report(error);
    } finally {
      setBusy(false);
    }
  };

  /** "🗑 Verwijderen" in the bar: the chosen orders in one go, with the owner's code once. */
  const deleteMany = async () => {
    if (selected.length === 0) return;
    const authorization = await authorize("orders.delete", t.codes.delete);
    if (authorization) setOpen({ kind: "deleteMany", orderIds: selected.map((order) => order.id), authorization });
  };

  /**
   * "Betaalwijze" on a row: straight to the choice, without a code. An order of Takeaway or Uber Eats only with
   * the owner's own code (Furkan, 17/10/2026).
   */
  const changeMethod = async (order: Order) => {
    only(order);
    if (!correctablePlatform(order)) {
      setOpen({ kind: "method", orderId: order.id });
      return;
    }
    const authorization = await authorize("owner", t.codes.platformMethod);
    if (authorization) setOpen({ kind: "method", orderId: order.id, authorization });
  };

  /**
   * "Online betaald" on an open order of Takeaway or Uber Eats (Antalya, 17/10/2026: "⚠ Betaling nakijken" and it
   * was paid on the platform after all): with the owner's own code, the whole amount online; the courier collects
   * nothing.
   */
  const markPaidOnline = async (order: Order) => {
    if (busy) return;
    only(order);
    const authorization = await authorize("owner", t.codes.platformPaidOnline);
    if (!authorization) return;
    setBusy(true);
    try {
      const updated = await api.request<Order>("POST", `/orders/${order.id}/paid-online`, {
        staffToken: authorization.token,
        body: {},
      });
      state.upsert(updated);
      toast(t.orders.paidOnlineDone(order.code));
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  /** "➜ Navigeer" (was "Route starten", Furkan 10/10/2026): the first 9 deliveries of the selection, in the order of the shortest drive. */
  const startRoute = async () => {
    if (routable.length === 0 || busy) return;
    setBusy(true);
    try {
      const answer = await api.request<{ route: Route }>("POST", "/device/route", {
        body: { orderIds: routable.slice(0, MAX_ROUTE).map((order) => order.id) },
      });
      setOpen({ kind: "route", route: answer.route });
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  /**
   * "🖨 Afdrukken" (prototype "Bonnen"): printed on the receipt printer; one receipt then shows as after ordering
   * ("Afgedrukt op …"), several say "3 bonnen afgedrukt op …". A shop without a receipt printer prints
   * from the browser: one receipt in its window, several together, each on its own page.
   */
  const printReceipts = async () => {
    if (selected.length === 0 || busy) return;
    setBusy(true);
    try {
      const prints = await printAgain(
        api,
        selected.map((order) => order.id),
      );
      if (selected.length === 1) {
        setOpen({ kind: "receipt", orderId: selected[0]!.id, printed: prints[0] });
      } else if (prints.length > 0) {
        const said = reprintToast(prints, "");
        if (said !== null) toast(said);
      } else {
        setOpen({ kind: "receipts", orderIds: selected.map((order) => order.id) });
      }
    } catch (error) {
      report(error);
    } finally {
      setBusy(false);
    }
  };

  const onRow = (event: MouseEvent<HTMLTableRowElement>, order: Order) => {
    if (event.target instanceof Element && event.target.closest("button, input, a")) return;
    setChosen(clickRow(selection, ids, order.id, { shift: event.shiftKey, toggle: event.ctrlKey || event.metaKey }));
  };

  const routeHint =
    routable.length > MAX_ROUTE
      ? t.orders.selection.routeMax
      : routable.length > 1
        ? t.orders.selection.routeMany
        : routable.length === 1
          ? t.orders.selection.routeOne
          : "";
  const help = admin ? t.orders.helpSelectAdmin : t.orders.helpSelect;

  return (
    <div className="orders">
      <section className="panel orders-list" aria-label={t.orders.title}>
        <div className="list-head">
          <h2>{admin ? t.orders.adminTitle : t.orders.title}</h2>
          {day !== undefined && (
            <div className="daypick" role="group" aria-label={t.orders.day.label}>
              <button
                type="button"
                className="btn small"
                aria-label={t.orders.day.previous}
                onClick={() => {
                  setChosen(NOTHING);
                  day.onChange(shiftDay(day.value, -1));
                }}
              >
                ‹
              </button>
              <input
                type="date"
                aria-label={t.orders.day.label}
                value={day.value}
                max={day.today}
                onChange={(event) => {
                  const picked = event.target.value;
                  if (picked === "" || picked > day.today) return;
                  setChosen(NOTHING);
                  day.onChange(picked);
                }}
              />
              <button
                type="button"
                className="btn small"
                aria-label={t.orders.day.next}
                disabled={!earlier}
                onClick={() => {
                  setChosen(NOTHING);
                  day.onChange(shiftDay(day.value, 1));
                }}
              >
                ›
              </button>
              {earlier && (
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => {
                    setChosen(NOTHING);
                    day.onChange(day.today);
                  }}
                >
                  {t.orders.day.today}
                </button>
              )}
            </div>
          )}
          {admin && ownerBeheer !== undefined && (
            <button
              type="button"
              className="tiny-gear"
              aria-label={t.orders.ownerTools}
              aria-pressed={showDelete}
              title={t.orders.ownerTools}
              onClick={() => {
                if (showDelete) setAdminCode(null);
                else setOpen({ kind: "adminCode" });
              }}
            >
              ⚙
            </button>
          )}
        </div>
        {admin && (
          <p className="note">
            {earlier ? t.orders.day.past(formatDate(`${day.value}T12:00:00`)) : t.orders.adminNote}
          </p>
        )}
        {earlier && !state.loaded && <p className="note">{t.orders.day.loading}</p>}

        {/* the selection bar (prototype routebar ordbar) */}
        <div className="routebar ordbar" role="toolbar" aria-label={t.orders.selection.bar}>
          {/* what is chosen and "selectie wissen" together, the buttons together (Furkan, 10/10/2026:
              "1 geselecteerd en selectie wissen naast elkaar, afdrukken en navigeer naast elkaar") */}
          <span className="ordsel">
            <span className="ordcount">
              {selected.length === 0 ? (
                // in Beheer het totaal van de getoonde lijst tonen (Furkan, 15/10/2026)
                admin && shown.length > 0 ? (
                  t.orders.selection.listTotal(shownActive.length, formatEuro(listTotalCents))
                ) : (
                  t.orders.selection.none
                )
              ) : (
                <>
                  <b>{selected.length}</b> {t.orders.selection.count}
                  {/* the total of several orders only in Beheer: not for everyone at the till (Furkan, 09/10/2026) */}
                  {admin && <> · {formatEuro(active.reduce((total, order) => total + order.totalCents, 0))}</>}
                </>
              )}
            </span>
            {selected.length > 0 && (
              <button
                type="button"
                className="linkbtn proto"
                onClick={() => {
                  setChosen(NOTHING);
                }}
              >
                {t.orders.selection.clear}
              </button>
            )}
          </span>
          <span className="ordacts">
            <button
              type="button"
              className="btn"
              disabled={selected.length === 0 || busy}
              onClick={() => void printReceipts()}
            >
              {t.orders.selection.receipts(selected.length)}
            </button>
            {/* no "Betaalwijze wijzigen" for several orders here (Furkan, 10/10/2026: "hier bovenaan
                betaalwijze wijzigen eruit"): every row has its own Betaalwijze */}
            {!admin && (
              <button
                type="button"
                className="btn primary"
                disabled={routable.length === 0 || busy}
                onClick={() => void startRoute()}
              >
                {t.orders.selection.route(routable.length)}
              </button>
            )}
            {admin && showDelete && (
              // only behind the small ⚙ and the special admin code, like Verwijderen on a row
              <button
                type="button"
                className="btn danger"
                disabled={!selected.some(canDelete) || busy}
                title={selected.some(canDelete) || selected.length === 0 ? undefined : t.orders.deleteRefused}
                onClick={() => void deleteMany()}
              >
                {t.orders.selection.delete(selected.filter(canDelete).length)}
              </button>
            )}
          </span>
          {!admin && routeHint !== "" && <span className="proto">{routeHint}</span>}
        </div>

        {state.orders.length > 0 && (
          // a phone has no heads of the columns, so no box "Alles selecteren" there: it stands next to Filter
          // (Furkan, 27/10/2026: "burada neden herşeyi selecteren yapamıyor?")
          <div className="ordphonebar">
            <button
              type="button"
              className={`btn ordfilterbtn phone-only${filtered ? " on" : ""}`}
              aria-expanded={filtersOpen}
              onClick={() => {
                setFiltersOpen(!filtersOpen);
              }}
            >
              {t.orders.filters.button}
              {filtered && (
                <span className="fcount">
                  {[
                    ...(kind === "all" ? [] : [t.orders.filters.kinds[kind]]),
                    ...(method === "all" ? [] : [t.orders.filters.methods[method]]),
                  ].join(" · ")}
                </span>
              )}
            </button>
            {shown.length > 0 && (
              <label className="btn ordallbtn">
                <input
                  type="checkbox"
                  className="rchk"
                  checked={allOn}
                  onChange={(event) => {
                    setChosen(tickAll(selection, ids, event.target.checked));
                  }}
                />
                {t.orders.selection.all}
              </label>
            )}
          </div>
        )}
        {state.orders.length > 0 && (
          <div className={filtersOpen ? "ordfilters open" : "ordfilters"}>
            <div className="seg" role="group" aria-label={t.orders.filters.kind}>
              {KIND_FILTERS.map((each) => (
                <button
                  key={each}
                  type="button"
                  aria-pressed={kind === each}
                  onClick={() => {
                    setKind(each);
                  }}
                >
                  {t.orders.filters.kinds[each]}
                  {each !== "all" && (
                    <span className="fcount">{state.orders.filter((order) => matchesKind(order, each)).length}</span>
                  )}
                </button>
              ))}
            </div>
            <div className="seg" role="group" aria-label={t.orders.filters.method}>
              {METHOD_FILTERS.map((each) => (
                <button
                  key={each}
                  type="button"
                  aria-pressed={method === each}
                  onClick={() => {
                    setMethod(each);
                  }}
                >
                  {t.orders.filters.methods[each]}
                </button>
              ))}
            </div>
          </div>
        )}
        {state.loaded && state.orders.length === 0 && <p className="empty">{t.orders.empty}</p>}
        {state.orders.length > 0 && shown.length === 0 && filtered && (
          <p className="empty">
            {t.orders.filters.none}{" "}
            <button
              type="button"
              className="linkbtn"
              onClick={() => {
                setKind("all");
                setMethod("all");
              }}
            >
              {t.orders.filters.reset}
            </button>
          </p>
        )}
        {shown.length > 0 && (
          <table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="rchk"
                    checked={allOn}
                    aria-label={t.orders.selection.all}
                    onChange={(event) => {
                      setChosen(tickAll(selection, ids, event.target.checked));
                    }}
                  />
                </th>
                <th>{t.orders.time}</th>
                <th>{t.orders.code}</th>
                <th>{t.orders.channel}</th>
                <th>{t.orders.address}</th>
                <th className="r">{t.orders.amount}</th>
                <th>{t.orders.payment}</th>
                <th>{t.orders.status}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {shown.map((order) => {
                const actions = actionsFor(order);
                const on = selection.ids.has(order.id);
                return (
                  <tr
                    key={order.id}
                    className={`orow ${state.recent.has(order.id) ? "flash" : ""} ${on ? "rsel" : ""} ${
                      order.status === "cancelled" ? "gone" : ""
                    }`}
                    onMouseDown={(event) => {
                      // Shift-click chooses a range: no text selection
                      if (event.shiftKey) event.preventDefault();
                    }}
                    onClick={(event) => {
                      onRow(event, order);
                    }}
                    onDoubleClick={(event) => {
                      if (event.target instanceof Element && event.target.closest("button, input, a")) return;
                      if (!admin && actions.edit) void startEdit(order);
                    }}
                  >
                    <td className="chkcell">
                      <input
                        type="checkbox"
                        className="rchk"
                        checked={on}
                        aria-label={t.orders.selection.row(order.code)}
                        onChange={(event) => {
                          setChosen(tickRow(selection, order.id, event.target.checked));
                        }}
                      />
                    </td>
                    <td className="num timecell">{formatTime(order.createdAt)}</td>
                    <td className="code">{order.code}</td>
                    <td className="kindcell">
                      {kindOf(order)}
                      {/* an order of a platform carries its logo (Furkan, 15/10/2026) */}
                      {order.platform !== null ? (
                        <PlatformLogo platform={order.platform} />
                      ) : (
                        order.source === "online" && <span className="pill warn">{t.orders.online}</span>
                      )}
                    </td>
                    {/* its own column (Furkan, 07/10/2026): an address is found at a glance. No column
                        Inhoud any more (Furkan, 10/10/2026: "inhoud'a hiç gerek yok, listeyi gereksiz
                        uzatıyor"): what is in it is on the receipt beside the list; what was changed
                        stays here */}
                    <td className="addresscell">
                      {addressOf(order)}
                      {/* an order of Mymenu.be, Takeaway.com or Uber Eats is hardly ever changed: no ✎ notes in the list
                          (Furkan, 16/10/2026); its history stays on the order */}
                      {order.platform === null && <EditNotes order={order} />}
                    </td>
                    <td className="r num amountcell">{formatEuro(order.totalCents)}</td>
                    {/* on a phone the row is two lines (Furkan, 10/10/2026): this ends the first */}
                    <td className="rowbreak" aria-hidden="true" />
                    <td className="paycell">
                      {paymentOf(order)}
                      <MethodChangeNote order={order} />
                    </td>
                    <td className="statuscell">
                      <StatusPill order={order} />
                    </td>
                    <td className="actcell">
                      <div className="acts">
                        {!admin && actions.edit && (
                          <button type="button" className="btn small" onClick={() => void startEdit(order)}>
                            {t.orders.edit}
                          </button>
                        )}
                        {!admin && actions.pay && (
                          <button
                            type="button"
                            className="btn small primary"
                            onClick={() => {
                              only(order);
                              setOpen({ kind: "pay", orderId: order.id });
                            }}
                          >
                            {t.orders.pay}
                          </button>
                        )}
                        {!admin && order.mails > 0 && (
                          <button
                            type="button"
                            className="btn small"
                            aria-label={t.orders.mails.buttonLabel(order.mails)}
                            onClick={() => {
                              only(order);
                              setOpen({ kind: "mails", orderId: order.id });
                            }}
                          >
                            {t.orders.mails.button(order.mails)}
                          </button>
                        )}
                        {!admin && actions.changeMethod && (
                          <button
                            type="button"
                            className="btn small"
                            onClick={() => {
                              void changeMethod(order);
                            }}
                          >
                            {t.orders.changeMethod}
                          </button>
                        )}
                        {!admin && actions.paidOnline && (
                          <button
                            type="button"
                            className="btn small"
                            disabled={busy}
                            title={t.orders.paidOnlineTitle}
                            onClick={() => void markPaidOnline(order)}
                          >
                            {t.orders.paidOnline}
                          </button>
                        )}
                        {/* Annuleren is under the receipt on the right (decision), not on the row */}
                        {admin && !earlier && (
                          <button
                            type="button"
                            className="btn small danger ghost"
                            disabled={!actions.cancel}
                            title={actions.cancel ? t.orders.correctAllowed : t.orders.correctRefused}
                            onClick={() => void withCode("correct", order)}
                          >
                            {t.orders.cancel}
                          </button>
                        )}
                        {admin && showDelete && order.channel === "table" && order.status !== "cancelled" && (
                          <button
                            type="button"
                            className="btn small"
                            disabled={busy || order.source !== "till"}
                            title={order.source === "till" ? t.orders.toTakeaway.title : t.orders.toTakeaway.online}
                            onClick={() => void toTakeaway(order)}
                          >
                            {t.orders.toTakeaway.button}
                          </button>
                        )}
                        {admin && showDelete && (
                          <button
                            type="button"
                            className="btn small danger"
                            disabled={!canDelete(order)}
                            title={canDelete(order) ? t.orders.deleteAllowed : t.orders.deleteRefused}
                            onClick={() => void withCode("delete", order)}
                          >
                            {t.orders.delete}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="note help">
          <b>{help[0]}</b> {help[1]}
          <br />
          <b>{t.orders.help[0]}</b> {t.orders.help[1]} {admin ? t.orders.helpDeleteAdmin : t.orders.helpDelete}
        </p>
      </section>

      {selected.length > 1 ? (
        <OrdersOverview orders={selected} onOnly={only} totals={admin} />
      ) : selected.length === 1 ? (
        <OrderPanel
          key={selected[0]!.id}
          order={selected[0]!}
          onEdit={(order) => void startEdit(order)}
          onCancel={(order) => void withCode("cancel", order)}
          readOnly={admin}
        />
      ) : (
        <aside className="bonpanel">
          <div className="ordempty">
            <b>{t.orders.selection.emptyTitle}</b>
            <span className="proto">
              {touchScreen() ? t.orders.selection.emptyHintTouch : t.orders.selection.emptyHint}
            </span>
          </div>
        </aside>
      )}

      {open?.kind === "pay" && dialogOrder && (
        <CheckoutDialog
          target={{ kind: "existing", order: dialogOrder }}
          onPaid={(paid) => {
            // prototype: "Kassabon → …", "Kassalade open (contant)"
            for (const text of printToasts(paid.prints)) toast(text);
            state.upsert(paid);
            close();
          }}
          onClose={close}
        />
      )}
      {open?.kind === "cancel" && dialogOrder && (
        <CancelDialog order={dialogOrder} authorization={open.authorization} onDone={state.upsert} onClose={close} />
      )}
      {open?.kind === "adminCode" && ownerBeheer !== undefined && (
        <AdminCodeDialog
          beheer={ownerBeheer}
          onUnlocked={(code) => {
            setAdminCode(code);
            close();
          }}
          onClose={close}
        />
      )}
      {open?.kind === "delete" && dialogOrder && adminCode !== null && (
        <DeleteDialog
          order={dialogOrder}
          authorization={open.authorization}
          adminCode={adminCode}
          onAdminCodeRefused={() => {
            setAdminCode(null);
          }}
          onDeleted={() => {
            close();
            state.reload();
          }}
          onClose={close}
        />
      )}
      {open?.kind === "deleteMany" && adminCode !== null && (
        <DeleteManyDialog
          orders={state.orders.filter((order) => open.orderIds.includes(order.id))}
          authorization={open.authorization}
          adminCode={adminCode}
          onAdminCodeRefused={() => {
            setAdminCode(null);
          }}
          onChanged={state.reload}
          onClose={close}
        />
      )}
      {open?.kind === "correct" && dialogOrder && (
        <CorrectionDialog
          order={dialogOrder}
          authorization={open.authorization}
          onDone={state.upsert}
          onClose={close}
        />
      )}
      {open?.kind === "method" && dialogOrder && (
        <PaymentMethodDialog
          order={dialogOrder}
          authorization={open.authorization}
          onDone={state.upsert}
          onClose={close}
        />
      )}
      {open?.kind === "route" && (
        <RouteDialog
          route={open.route}
          onGiven={(orders) => {
            orders.forEach(state.upsert);
            setChosen(NOTHING);
          }}
          onClose={close}
        />
      )}
      {open?.kind === "receipt" && dialogOrder && (
        <ReceiptDialog order={dialogOrder} printed={open.printed} onClose={close} />
      )}
      {open?.kind === "mails" && dialogOrder && <MailsDialog order={dialogOrder} onClose={close} />}
      {open?.kind === "receipts" && (
        <ReceiptsDialog orders={open.orderIds.flatMap((id) => byId(id) ?? [])} onClose={close} />
      )}
    </div>
  );
}
